import { AddCircle } from '@mui/icons-material'
import {
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
  styled
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useBucketPlan from '../../../hooks/useBucketPlan'
import CreateAlbumForm from './CreateAlbumForm'
import useBucket from '../../../hooks/useBucket'
import useBucketMigrationProgress from '../../../hooks/useBucketMigrationProgress'
import useBucketConvertProgress from '../../../hooks/useBucketConvertProgress'

interface Props {
  bucketId: string
}

export const AlbumLink = styled(Button)(({ theme }) => ({
  display: 'block',
  width: '100%',
  fontSize: '1rem',
  textAlign: 'left',
  color: 'inherit',
  '&:hover': { color: theme.palette.primary.main, backgroundColor: 'transparent' }
}))

export default function AlbumSubHeader({ bucketId }: Readonly<Props>) {
  const { bucketUser } = useBucket()
  const { restrictions, tooltipTitle } = useBucketPlan()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const isMigrationInProgressForBucket = useBucketMigrationProgress()
  const isBucketConvertInProgressForBucket = useBucketConvertProgress()

  const restricted = restrictions.modifyAlbums
  const disabled = !bucketUser?.permissions?.albumCreate
  const hideTooltip = restricted === false && disabled === false
  let title = tooltipTitle('modifyAlbums')
  if (isMigrationInProgressForBucket || isBucketConvertInProgressForBucket) {
    title = 'Album create is disabled during the migration process'
  } else if (restricted) {
    title = 'Your viewer role for this bucket does not allow you to create albums.'
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const onClickAlbum = () => {
    navigate(`/bucket/${bucketId}/albums`)
  }

  if (!bucketId) {
    return null
  }

  return (
    <>
      <ListItem disablePadding>
        <ListItemText
          primary={
            <AlbumLink onClick={onClickAlbum} data-test="drawer-albums-title-btn" sx={{ padding: 0 }}>
              Albums
            </AlbumLink>
          }
        />
        <Tooltip
          PopperProps={{
            disablePortal: hideTooltip
          }}
          disableFocusListener={hideTooltip}
          disableHoverListener={hideTooltip}
          disableTouchListener={hideTooltip}
          title={title}
        >
          <ListItemSecondaryAction sx={{ right: 0 }}>
            <Collapse in={!open}>
              <IconButton
                disabled={disabled || restricted || isMigrationInProgressForBucket || isBucketConvertInProgressForBucket}
                onClick={handleOpen}
                data-testid="create-album-action-button"
                size="small"
                color="primary"
              >
                <AddCircle fontSize="small" />
              </IconButton>
            </Collapse>
          </ListItemSecondaryAction>
        </Tooltip>
      </ListItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ flex: 1 }}>
            Create Album
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: 3, paddingTop: 0 }}>
          <CreateAlbumForm bucketId={bucketId} handleClose={handleClose} />
        </DialogContent>
      </Dialog>
    </>
  )
}
